
import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { Truck, SiteSettings, BlogPost, Inquiry, EmailConfig, EmailTemplate, EmailLog, Subscriber } from '../types';
import { TRUCKS } from '../constants';
import { defaultSettings } from '../config';
import { db } from '../firebase';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  query,
  writeBatch
} from 'firebase/firestore';

interface DataContextType {
  trucks: Truck[];
  settings: SiteSettings;
  posts: BlogPost[];
  inquiries: Inquiry[];
  emailConfig: EmailConfig;
  emailTemplates: EmailTemplate[];
  emailLogs: EmailLog[];
  subscribers: Subscriber[];
  isAuthenticated: boolean;
  isQuoteModalOpen: boolean;
  quoteModalType: 'quote' | 'financing';
  isLoading: boolean;

  // UI Actions
  openQuoteModal: (type?: 'quote' | 'financing') => void;
  closeQuoteModal: () => void;

  // Auth
  login: (password: string) => boolean;
  logout: () => void;
  updateAdminPassword: (password: string) => Promise<boolean>;

  // Inventory
  addTruck: (truck: Truck) => void;
  updateTruck: (truck: Truck) => void;
  deleteTruck: (id: string) => void;

  // Settings
  updateSettings: (settings: SiteSettings) => void;
  resetSettings: () => void;

  // Blog
  addPost: (post: BlogPost) => void;
  updatePost: (post: BlogPost) => void;
  deletePost: (id: string) => void;

  // Inquiries
  addInquiry: (inquiry: Inquiry) => void;
  updateInquiryStatus: (id: string, status: Inquiry['status']) => void;
  deleteInquiry: (id: string) => void;

  // Email System
  updateEmailConfig: (config: EmailConfig) => void;
  saveEmailTemplate: (template: EmailTemplate) => void;
  sendEmail: (to: string, subject: string, body: string, templateId?: string) => Promise<boolean>;
  deleteEmailLog: (id: string) => void;
}

const defaultEmailConfig: EmailConfig = {
  smtpHost: 'smtp.gmail.com',
  smtpPort: 587,
  encryption: 'TLS',
  username: 'sales@automax.co.tz',
  senderName: 'Automax Sales Team',
  senderEmail: 'sales@automax.co.tz',
  dailyLimit: 500
};

const initialTemplates: EmailTemplate[] = [
  { id: '1', name: 'Welcome Email', subject: 'Welcome to Automax Family', body: 'Hi {name}, thanks for joining us...', variables: ['{name}'] },
  { id: '2', name: 'Quote Received', subject: 'We received your quote request', body: 'Hi {name}, we are reviewing your request for...', variables: ['{name}'] }
];

const initialPosts: BlogPost[] = [
  {
    id: '1',
    title: '5 Tips for Maintaining Heavy Duty Trucks in East Africa',
    slug: '5-tips-maintaining-heavy-duty-trucks',
    excerpt: 'Learn how to extend the lifespan of your fleet given the unique road conditions and climate of our region.',
    content: 'Maintenance is key to longevity. In East Africa, dust filters need changing twice as often...',
    author: 'John Doe',
    date: '2024-05-15',
    image: 'https://images.unsplash.com/photo-1625232936711-66555eb3ce6c?auto=format&fit=crop&q=80&w=1000',
    category: 'Maintenance',
    tags: ['Maintenance', 'Tips', 'Safety'],
    status: 'Published',
    metaTitle: 'Truck Maintenance Tips East Africa',
    metaDescription: 'Expert tips for maintaining heavy duty trucks in Tanzanian road conditions.'
  },
  {
    id: '2',
    title: 'Why Shacman is Taking Over the Construction Sector',
    slug: 'why-shacman-taking-over-construction',
    excerpt: 'An in-depth look at the performance vs. price ratio of the new X3000 series.',
    content: 'The construction boom in Tanzania requires robust machinery. Shacman offers...',
    author: 'Sarah Smith',
    date: '2024-06-02',
    image: 'https://images.unsplash.com/photo-1590649774358-8686616428e3?auto=format&fit=crop&q=80&w=1000',
    category: 'Reviews',
    tags: ['Shacman', 'Construction', 'Reviews'],
    status: 'Published'
  }
];

const DataContext = createContext<DataContextType | undefined>(undefined);

export const DataProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // --- STATE INITIALIZATION ---
  const [trucks, setTrucks] = useState<Truck[]>([]);
  const [settings, setSettings] = useState<SiteSettings>(defaultSettings);
  const [adminPassword, setAdminPassword] = useState('admin123');
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [inquiries, setInquiries] = useState<Inquiry[]>([]);
  const [emailConfig, setEmailConfigState] = useState<EmailConfig>(defaultEmailConfig);
  const [emailTemplates, setEmailTemplates] = useState<EmailTemplate[]>(initialTemplates);
  const [emailLogs, setEmailLogs] = useState<EmailLog[]>([]);
  const [subscribers, setSubscribers] = useState<Subscriber[]>([]);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);
  const [quoteModalType, setQuoteModalType] = useState<'quote' | 'financing'>('quote');
  const [isLoading, setIsLoading] = useState(true);

  // --- FIRESTORE INITIALIZATION ---
  useEffect(() => {
    const initializeData = async () => {
      try {
        setIsLoading(true);

        // Load Settings
        // Load Settings
        const settingsDoc = await getDoc(doc(db, 'settings', 'site'));
        let currentSettings = defaultSettings;
        if (settingsDoc.exists()) {
          const data = settingsDoc.data() as SiteSettings;
          currentSettings = { ...defaultSettings, ...data };
          setSettings(currentSettings);
        } else {
          await setDoc(doc(db, 'settings', 'site'), defaultSettings);
        }

        const isDataInitialized = currentSettings.dataInitialized;

        // Load Admin Password
        const adminDoc = await getDoc(doc(db, 'admin', 'credentials'));
        if (adminDoc.exists()) {
          setAdminPassword(adminDoc.data().password || 'admin123');
        } else {
          await setDoc(doc(db, 'admin', 'credentials'), { password: 'admin123' });
        }

        // Load Trucks
        // Load Trucks
        const trucksSnapshot = await getDocs(collection(db, 'trucks'));
        if (!trucksSnapshot.empty) {
          const trucksData = trucksSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Truck));
          setTrucks(trucksData);
        } else if (!isDataInitialized) {
          // Initialize with default trucks ONLY if not initialized
          const batch = writeBatch(db);
          TRUCKS.forEach(truck => {
            batch.set(doc(db, 'trucks', truck.id), truck);
          });
          await batch.commit();
          setTrucks(TRUCKS);
        } else {
          setTrucks([]);
        }

        // Load Posts
        // Load Posts
        const postsSnapshot = await getDocs(collection(db, 'posts'));
        if (!postsSnapshot.empty) {
          const postsData = postsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as BlogPost));
          setPosts(postsData);
        } else if (!isDataInitialized) {
          // Initialize with default posts ONLY if not initialized
          const batch = writeBatch(db);
          initialPosts.forEach(post => {
            batch.set(doc(db, 'posts', post.id), post);
          });
          await batch.commit();
          setPosts(initialPosts);
        } else {
          setPosts([]);
        }

        // Load Inquiries
        const inquiriesSnapshot = await getDocs(collection(db, 'inquiries'));
        const inquiriesData = inquiriesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Inquiry));
        setInquiries(inquiriesData);

        // Load Email Config
        const emailConfigDoc = await getDoc(doc(db, 'emailConfig', 'config'));
        if (emailConfigDoc.exists()) {
          setEmailConfigState(emailConfigDoc.data() as EmailConfig);
        } else {
          await setDoc(doc(db, 'emailConfig', 'config'), defaultEmailConfig);
        }

        // Load Email Templates
        const templatesSnapshot = await getDocs(collection(db, 'emailTemplates'));
        if (!templatesSnapshot.empty) {
          const templatesData = templatesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as EmailTemplate));
          setEmailTemplates(templatesData);
        } else if (!isDataInitialized) {
          const batch = writeBatch(db);
          initialTemplates.forEach(template => {
            batch.set(doc(db, 'emailTemplates', template.id), template);
          });
          await batch.commit();
          setEmailTemplates(initialTemplates);
        } else {
          setEmailTemplates([]);
        }

        // Mark as initialized if it wasn't
        if (!isDataInitialized) {
          await setDoc(doc(db, 'settings', 'site'), {
            ...currentSettings,
            dataInitialized: true
          });
          setSettings({ ...currentSettings, dataInitialized: true });
        }

        // Load Email Logs
        const logsSnapshot = await getDocs(collection(db, 'emailLogs'));
        const logsData = logsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as EmailLog));
        setEmailLogs(logsData);

        // Load Subscribers
        const subscribersSnapshot = await getDocs(collection(db, 'subscribers'));
        const subscribersData = subscribersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Subscriber));
        setSubscribers(subscribersData);

        setIsLoading(false);
      } catch (error) {
        console.error('Error initializing Firebase data:', error);
        setIsLoading(false);
      }
    };

    initializeData();

    // Real-time listeners for critical data
    const unsubSettings = onSnapshot(doc(db, 'settings', 'site'), (doc) => {
      if (doc.exists()) {
        setSettings({ ...defaultSettings, ...doc.data() as SiteSettings });
      }
    });

    const unsubTrucks = onSnapshot(collection(db, 'trucks'), (snapshot) => {
      const trucksData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Truck));
      setTrucks(trucksData);
    });

    const unsubPosts = onSnapshot(collection(db, 'posts'), (snapshot) => {
      const postsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as BlogPost));
      setPosts(postsData);
    });

    return () => {
      unsubSettings();
      unsubTrucks();
      unsubPosts();
    };
  }, []);

  // --- ACTIONS ---
  const openQuoteModal = (type: 'quote' | 'financing' = 'quote') => {
    setQuoteModalType(type);
    setIsQuoteModalOpen(true);
  };

  const closeQuoteModal = () => {
    setIsQuoteModalOpen(false);
  };

  const login = (password: string): boolean => {
    if (password === adminPassword) {
      setIsAuthenticated(true);
      return true;
    }
    return false;
  };

  const logout = () => {
    setIsAuthenticated(false);
  };

  const updateAdminPassword = async (password: string): Promise<boolean> => {
    setAdminPassword(password);
    try {
      await setDoc(doc(db, 'admin', 'credentials'), { password }, { merge: true });
      return true;
    } catch (error) {
      console.error('Error updating admin password in DB:', error);
      return false;
    }
  };

  // --- INVENTORY ---
  const addTruck = async (truck: Truck) => {
    try {
      await setDoc(doc(db, 'trucks', truck.id), truck);
    } catch (error) {
      console.error('Error adding truck to DB:', error);
    }
    // Update local state regardless of DB success
    setTrucks(prev => [...prev, truck]);
  };

  const updateTruck = async (truck: Truck) => {
    try {
      await updateDoc(doc(db, 'trucks', truck.id), { ...truck });
    } catch (error) {
      console.error('Error updating truck in DB:', error);
    }
    setTrucks(prev => prev.map(t => t.id === truck.id ? truck : t));
  };

  const deleteTruck = async (id: string) => {
    try {
      await deleteDoc(doc(db, 'trucks', id));
    } catch (error) {
      console.error('Error deleting truck from DB:', error);
    }
    setTrucks(prev => prev.filter(t => t.id !== id));
  };

  // --- SETTINGS ---
  const updateSettings = async (newSettings: SiteSettings) => {
    try {
      await setDoc(doc(db, 'settings', 'site'), newSettings);
    } catch (error) {
      console.error('Error saving settings to DB:', error);
    }
    setSettings(newSettings);
  };

  const resetSettings = async () => {
    try {
      await setDoc(doc(db, 'settings', 'site'), defaultSettings);
    } catch (error) {
      console.error('Error resetting settings in DB:', error);
    }
    setSettings(defaultSettings);
  };

  // --- BLOG ---
  const addPost = async (post: BlogPost) => {
    try {
      await setDoc(doc(db, 'posts', post.id), post);
    } catch (error) {
      console.error('Error adding post to DB:', error);
    }
    setPosts(prev => [...prev, post]);
  };

  const updatePost = async (post: BlogPost) => {
    try {
      await updateDoc(doc(db, 'posts', post.id), { ...post });
    } catch (error) {
      console.error('Error updating post in DB:', error);
    }
    setPosts(prev => prev.map(p => p.id === post.id ? post : p));
  };

  const deletePost = async (id: string) => {
    try {
      await deleteDoc(doc(db, 'posts', id));
    } catch (error) {
      console.error('Error deleting post from DB:', error);
    }
    setPosts(prev => prev.filter(p => p.id !== id));
  };

  // --- INQUIRIES ---
  const addInquiry = async (inquiry: Inquiry) => {
    try {
      await setDoc(doc(db, 'inquiries', inquiry.id), inquiry);
    } catch (error) {
      console.error('Error adding inquiry to DB (simulating success locally):', error);
    }
    setInquiries(prev => [...prev, inquiry]);
  };

  const updateInquiryStatus = async (id: string, status: Inquiry['status']) => {
    try {
      await updateDoc(doc(db, 'inquiries', id), { status });
    } catch (error) {
      console.error('Error updating inquiry status in DB:', error);
    }
    setInquiries(prev => prev.map(inq => inq.id === id ? { ...inq, status } : inq));
  };

  const deleteInquiry = async (id: string) => {
    try {
      await deleteDoc(doc(db, 'inquiries', id));
    } catch (error) {
      console.error('Error deleting inquiry from DB:', error);
    }
    setInquiries(prev => prev.filter(inq => inq.id !== id));
  };

  // --- EMAIL SYSTEM ---
  const updateEmailConfig = async (config: EmailConfig) => {
    try {
      await setDoc(doc(db, 'emailConfig', 'config'), config);
    } catch (error) {
      console.error('Error saving email config to DB:', error);
    }
    setEmailConfigState(config);
  };

  const saveEmailTemplate = async (template: EmailTemplate) => {
    try {
      await setDoc(doc(db, 'emailTemplates', template.id), template);
    } catch (error) {
      console.error('Error saving email template to DB:', error);
    }
    setEmailTemplates(prev => {
      const exists = prev.find(t => t.id === template.id);
      if (exists) {
        return prev.map(t => t.id === template.id ? template : t);
      }
      return [...prev, template];
    });
  };

  const sendEmail = async (to: string, subject: string, body: string, templateId?: string): Promise<boolean> => {
    const log: EmailLog = {
      id: Date.now().toString(),
      recipient: to,
      subject,
      body,
      type: 'general',
      sentAt: new Date().toISOString(),
      status: 'Sent',
      templateId
    };

    try {
      await setDoc(doc(db, 'emailLogs', log.id), log);
    } catch (error) {
      console.error('Error saving email log to DB:', error);
    }
    setEmailLogs(prev => [...prev, log]);
    return true;
  };

  const deleteEmailLog = async (id: string) => {
    try {
      await deleteDoc(doc(db, 'emailLogs', id));
    } catch (error) {
      console.error('Error deleting email log from DB:', error);
    }
    setEmailLogs(prev => prev.filter(log => log.id !== id));
  };

  const value: DataContextType = {
    trucks,
    settings,
    posts,
    inquiries,
    emailConfig,
    emailTemplates,
    emailLogs,
    subscribers,
    isAuthenticated,
    isQuoteModalOpen,
    quoteModalType,
    isLoading,
    openQuoteModal,
    closeQuoteModal,
    login,
    logout,
    updateAdminPassword,
    addTruck,
    updateTruck,
    deleteTruck,
    updateSettings,
    resetSettings,
    addPost,
    updatePost,
    deletePost,
    addInquiry,
    updateInquiryStatus,
    deleteInquiry,
    updateEmailConfig,
    saveEmailTemplate,
    sendEmail,
    deleteEmailLog
  };

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
};

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) throw new Error('useData must be used within DataProvider');
  return context;
};
